<script setup lang="ts">
import { gameWinnerTileKey, type GameWinnerTileProps } from '@game/types'
import { computed, toRef, provide, useFormat, useGame, useCoreConfig, useDayjs, useClock } from '#imports'

const { asMoney, asRelativeTime } = useFormat()

const props = withDefaults(defineProps<GameWinnerTileProps>(), {
    isMegaPrize: false,
    isBigWin: false,
    variant: 'short',
    horizontal: false,
})

const winner = toRef(props, 'winner')
const isMegaPrize = toRef(props, 'isMegaPrize')
const isBingo = toRef(() => props.variant === 'bingo')
const isBingo90 = toRef(() => winner.value?.slug === 'bingo-90')
const icon = toRef(() => winner.value?.gameIconPath)
const dayjs = useDayjs()
const { clock } = useClock()

const { start } = useGame()

async function startGame() {
    await start({ gameSlug: winner.value.slug })
}

const wonAt = computed(() => {
    const timestamp = winner.value.wonAt || winner.value.created
    if (!timestamp) return dayjs(clock.value).tz()

    // Check if timestamp is in the future
    const wonTime = dayjs(timestamp).tz()
    const now = dayjs(clock.value).tz()

    return wonTime.valueOf() > now.valueOf() ? translations.durations.now : wonTime
})

const { translations } = useCoreConfig()
const wonTimeAgo = computed(() => {
    if (typeof wonAt.value === 'string') return wonAt.value

    return asRelativeTime(wonAt.value, {
        timeFormat: 'long',
        smallestUnit: 'seconds'
    })
})

const money = computed(() => asMoney(Number(winner.value.amount), { decimals: props.isBigWin || isBingo ? 0 : 2 }))

provide(gameWinnerTileKey, {
    winner,
    icon,
    money,
    wonTimeAgo,
    isMegaPrize,
    isBingo,
    startGame,
})
</script>

<template>
    <li
        class="winner-tile"
        :class="{
            'winner-tile--mega-prize': isMegaPrize,
            'winner-tile--big-win': props.isBigWin,
            'winner-tile--bingo': isBingo,
            'winner-tile--bingo-90': isBingo90,
            'winner-tile--horizontal': props.horizontal,
        }"
    >
        <template v-if="isBingo">
            <GameWinnerTileBingo />
        </template>
        <template v-else-if="variant === 'short' || horizontal">
            <GameWinnerTileShort />
        </template>
        <template v-else>
            <GameWinnerTileLong />
        </template>
    </li>
</template>

<style scoped lang="scss">
.winner-tile {
    list-style: none;
    position: relative;
    margin-bottom: 10px;
    padding: 8px;
    border-radius: $game-tile-border-radius;
    color: $game-winner-color;
    background: $game-winner-bg;
    font-size: $font-size-xs;
    cursor: pointer;
    backface-visibility: hidden;
    transform-style: preserve-3d;
    will-change: transform;

    &--mega-prize {
        color: $game-winner-mega-prize-color;
        background: $game-winner-mega-prize-bg;
        grid-template-areas: 'image user gamename' 'image amount time';
    }

    &--bingo {
        color: $game-winner-bingo-color;
        background: $game-winner-bingo-bg;
        border: $game-winner-bingo-border;

        @media (min-width: $screen-md) {
            min-width: 137px;
            margin: 0 8px;
            padding: 5px;

            &:first-child {
                margin-left: 0;
            }
        }

        &:nth-child(1n + 5),
        &:nth-child(1n + 6) {
            display: none;
        }

        &:nth-child(1n + 5) {
            @media (min-width: $screen-xl) {
                display: block;
            }
        }

        &:nth-child(1n + 4) {
            // Tablet portrait only
            @media (min-width: $screen-md) and (max-width: #{$screen-lg - 1px}) {
                display: none;
            }

            @media (min-width: $screen-xl) {
                display: block;
            }
        }

        &.winner-tile--mega-prize {
            min-width: 224px;
            max-width: 60vw;
            background: $game-winner-bingo-mega-prize-bg;
            box-shadow: 0 6px 16px $game-winner-bingo-mega-prize-bg;
            border: $game-winner-bingo-mega-prize-border;
            transition-delay: 0.38s !important;

            @media (min-width: $screen-md) {
                width: 245px;
            }

            @media (min-width: $screen-lg) {
                height: 100%;
            }

            @media (min-width: $screen-xl) {
                transition-delay: 0.56s !important;
            }
        }
    }

    &--big-win {
        background: $game-winner-big-win-bg;
        color: $game-winner-big-win-color;
    }

    &--horizontal {
        margin-right: 10px;
        min-width: 160px;

        @media (min-width: $screen-md) {
            min-width: 180px;
        }
    }
}
</style>
